import { useEffect, useRef, useState } from "react"

import {
  getBusinessDocumentsContracts,
  getBusinessDocumentsProposals
} from "../api/business-documents.services.ts"
import type {
  BusinessDocumentContract,
  BusinessDocumentProposal,
  ContractStatusSummary,
  ProposalStatusSummary
} from "../types/business-documents.types.ts"

interface UseBusinessDocumentsProposalsParams {
  q: string
  status: string | null
  customerId?: string
}

interface UseBusinessDocumentsContractsParams {
  q: string
  status: string | null
}

interface BusinessDocumentsState<Row, Summary> {
  items: Row[]
  summary: Summary | null
  loading: boolean
  failed: boolean
}

/** Business Documents' Proposals tab — the real `Proposal` records, fetched
 * fresh on every filter change with a stale-response guard (a slower older
 * request must never overwrite a faster newer one). */
export function useBusinessDocumentsProposals(params: UseBusinessDocumentsProposalsParams) {
  const [state, setState] = useState<
    BusinessDocumentsState<BusinessDocumentProposal, ProposalStatusSummary>
  >({ items: [], summary: null, loading: true, failed: false })
  const latestRequest = useRef(0)

  useEffect(() => {
    const requestId = ++latestRequest.current
    setState((prev) => ({ ...prev, loading: true, failed: false }))
    void getBusinessDocumentsProposals({
      q: params.q || undefined,
      status: params.status ?? undefined,
      customer_id: params.customerId
    }).then((response) => {
      if (requestId !== latestRequest.current) return
      if (!response.ok || !response.data) {
        setState({ items: [], summary: null, loading: false, failed: true })
        return
      }
      setState({
        items: response.data.results,
        summary: response.data.status_summary,
        loading: false,
        failed: false
      })
    })
  }, [params.q, params.status, params.customerId])

  return state
}

/** Business Documents' Contracts tab — same shape, over `Contract`. */
export function useBusinessDocumentsContracts(params: UseBusinessDocumentsContractsParams) {
  const [state, setState] = useState<
    BusinessDocumentsState<BusinessDocumentContract, ContractStatusSummary>
  >({ items: [], summary: null, loading: true, failed: false })
  const latestRequest = useRef(0)

  useEffect(() => {
    const requestId = ++latestRequest.current
    setState((prev) => ({ ...prev, loading: true, failed: false }))
    void getBusinessDocumentsContracts({
      q: params.q || undefined,
      status: params.status ?? undefined
    }).then((response) => {
      if (requestId !== latestRequest.current) return
      if (!response.ok || !response.data) {
        setState({ items: [], summary: null, loading: false, failed: true })
        return
      }
      setState({
        items: response.data.results,
        summary: response.data.status_summary,
        loading: false,
        failed: false
      })
    })
  }, [params.q, params.status])

  return state
}
